import { type SubscriptionRepository } from "../interfaces";
import { type DatabaseSubscription } from "@/hooks/useSubscriptions";
import { supabase } from "@/integrations/supabase/supabaseClient";

export class SupabaseSubscriptionRepository implements SubscriptionRepository {
  private async getCurrentUserId(): Promise<string> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");
    return user.id;
  }

  async getAll(): Promise<DatabaseSubscription[]> {
    const userId = await this.getCurrentUserId();
    const { data, error } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data as DatabaseSubscription[];
  }

  async create(
    subscription: Omit<
      DatabaseSubscription,
      "id" | "user_id" | "created_at" | "updated_at"
    >,
  ): Promise<DatabaseSubscription> {
    const userId = await this.getCurrentUserId();
    const { data, error } = await supabase
      .from("subscriptions")
      .insert({
        ...subscription,
        user_id: userId,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async update(
    id: string,
    updates: Partial<DatabaseSubscription>,
  ): Promise<DatabaseSubscription> {
    const userId = await this.getCurrentUserId();
    const { data, error } = await supabase
      .from("subscriptions")
      .update(updates)
      .eq("id", id)
      .eq("user_id", userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async delete(id: string): Promise<void> {
    const userId = await this.getCurrentUserId();
    const { error } = await supabase
      .from("subscriptions")
      .delete()
      .eq("id", id)
      .eq("user_id", userId);

    if (error) throw error;
  }

  async bulkCreate(
    subscriptions: Omit<
      DatabaseSubscription,
      "id" | "user_id" | "created_at" | "updated_at"
    >[],
  ): Promise<DatabaseSubscription[]> {
    const userId = await this.getCurrentUserId();
    const subscriptionsWithUserId = subscriptions.map((sub) => ({
      ...sub,
      user_id: userId,
    }));

    const { data, error } = await supabase
      .from("subscriptions")
      .insert(subscriptionsWithUserId)
      .select();

    if (error) throw error;
    return data;
  }

  async deleteAll(): Promise<void> {
    const userId = await this.getCurrentUserId();
    const { error } = await supabase
      .from("subscriptions")
      .delete()
      .eq("user_id", userId);

    if (error) throw error;
  }
}
